import React, { useState, useEffect } from 'react';
import { knowledgeBaseApiService } from '../../services/knowledgeBaseApi';

interface SuggestedQuestionsProps {
  query: string;
  onQuestionClick: (question: string) => void;
  excludeId?: number;
  limit?: number;
}

export const SuggestedQuestions: React.FC<SuggestedQuestionsProps> = ({
  query,
  onQuestionClick,
  excludeId,
  limit = 5,
}) => {
  const [suggestedQuestions, setSuggestedQuestions] = useState<Array<{
    id: number;
    question: string;
    category: string;
    similarity: number;
  }>>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchSimilarQuestions = async () => {
      if (!query || query.trim().length < 3) {
        setSuggestedQuestions([]);
        return;
      }

      setIsLoading(true);
      try {
        const similar = await knowledgeBaseApiService.findSimilarQuestions(
          query,
          limit,
          excludeId
        );
        setSuggestedQuestions(similar);
      } catch (error) {
        console.error('Erreur lors de la récupération des questions similaires:', error);
        setSuggestedQuestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    // Délai pour éviter trop de requêtes
    const timer = setTimeout(fetchSimilarQuestions, 300);
    return () => clearTimeout(timer);
  }, [query, excludeId, limit]);

  if (isLoading) {
    return (
      <div className="mt-3 px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
          <span>Recherche de questions similaires...</span>
        </div>
      </div>
    );
  }

  if (suggestedQuestions.length === 0) {
    return null;
  }

  return (
    <div className="mt-3 px-4 py-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
      <div className="flex items-center space-x-2 mb-2">
        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        <h4 className="text-sm font-semibold text-green-800">Questions similaires :</h4>
      </div>
      <div className="space-y-2">
        {suggestedQuestions.map((item) => (
          <button
            key={item.id}
            onClick={() => onQuestionClick(item.question)}
            className="w-full text-left px-3 py-2 bg-white hover:bg-green-50 rounded-md border border-green-200 transition-colors duration-200 text-sm"
          >
            <div className="flex items-start justify-between">
              <span className="text-gray-700 flex-1">{item.question}</span>
              <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                {item.category}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

